import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalLayout, type LegalSection } from "@/components/legal-layout";
import { identificacaoEmpresa, LEGAL } from "@/lib/legal";

/** Política de Privacidade pública (URL cadastrada no app Meta). */
export const Route = createFileRoute("/privacidade")({
  component: PrivacidadePage,
});

const mail = (email: string) => (
  <a className="underline" href={`mailto:${email}`}>
    {email}
  </a>
);

const sections: LegalSection[] = [
  {
    title: "Quem somos",
    body: (
      <>
        <p>
          O {LEGAL.produto} é um sistema para agências e gestores de tráfego gerenciarem anúncios da
          Meta (Facebook e Instagram) de vários clientes. Ele é oferecido por{" "}
          {identificacaoEmpresa()}
          {LEGAL.endereco ? `, com sede em ${LEGAL.endereco}` : ""}.
        </p>
        <p>
          Esta política explica quais dados tratamos, por que, com quem compartilhamos e como você
          exerce seus direitos, conforme a Lei Geral de Proteção de Dados (Lei 13.709/2018, LGPD).
        </p>
      </>
    ),
  },
  {
    title: "Papéis no tratamento de dados",
    body: (
      <>
        <p>
          Para os dados da <strong>sua conta</strong> no {LEGAL.produto} (nome, e-mail, cobrança),
          nós somos o <strong>controlador</strong>.
        </p>
        <p>
          Para os dados dos <strong>clientes da sua agência</strong> e das pessoas que respondem aos
          seus anúncios (por exemplo, leads de formulários), a sua agência é a controladora e nós
          atuamos como <strong>operador</strong>, tratando os dados somente para prestar o serviço,
          conforme as suas instruções.
        </p>
      </>
    ),
  },
  {
    title: "Dados que coletamos",
    body: (
      <ul>
        <li>
          <strong>Cadastro:</strong> nome, e-mail, senha (guardada apenas como hash), nome da
          agência e membros convidados.
        </li>
        <li>
          <strong>Dados da Meta que você autoriza ao conectar sua conta:</strong> identificação do
          seu perfil, Business Managers, contas de anúncio, páginas, contas do Instagram vinculadas,
          pixels, públicos, campanhas, conjuntos, anúncios, criativos e métricas de desempenho, e
          leads dos formulários instantâneos das suas páginas. Pedimos apenas as permissões
          necessárias para cada função: ads_management, ads_read, business_management,
          pages_show_list, pages_read_engagement, pages_manage_ads, leads_retrieval e
          pages_manage_metadata.
        </li>
        <li>
          <strong>Arquivos que você envia:</strong> imagens, vídeos, listas de clientes (CSV) para
          públicos personalizados. As listas de clientes são normalizadas e convertidas em hash
          (SHA-256) no nosso servidor antes do envio à Meta; o arquivo original não é guardado.
        </li>
        <li>
          <strong>Cobrança:</strong> plano, status da assinatura e faturas. Dados de cartão são
          tratados diretamente pelo Stripe e nunca passam pelos nossos servidores.
        </li>
        <li>
          <strong>Uso e segurança:</strong> registros de acesso e de auditoria (quem alterou o quê e
          quando), endereço IP e informações do navegador.
        </li>
      </ul>
    ),
  },
  {
    title: "Para que usamos os dados",
    body: (
      <ul>
        <li>Prestar o serviço: exibir, criar e editar anúncios, relatórios, regras e alertas.</li>
        <li>
          Enviar leads aos destinos que você configurar (e-mail, webhook, planilha, WhatsApp).
        </li>
        <li>
          Cobrança, suporte, segurança, prevenção a fraudes e cumprimento de obrigações legais.
        </li>
        <li>
          Assistente de IA: quando você usa, enviamos apenas métricas agregadas e a estrutura das
          campanhas ao provedor de IA; não enviamos tokens, nem e-mails ou telefones de leads.
        </li>
      </ul>
    ),
  },
  {
    title: "Dados da Meta: o que não fazemos",
    body: (
      <ul>
        <li>Não vendemos, alugamos nem cedemos dados obtidos da Meta a terceiros.</li>
        <li>Não usamos dados da Meta para publicidade própria nem para criar perfis de pessoas.</li>
        <li>
          Não usamos dados de um cliente para atender outro: cada agência só acessa os próprios
          dados.
        </li>
        <li>
          Seguimos os Termos da Plataforma e as Políticas do Desenvolvedor da Meta. Nenhuma ação é
          feita na sua conta Meta sem uma ação sua ou uma regra automática criada por você.
        </li>
      </ul>
    ),
  },
  {
    title: "Com quem compartilhamos",
    body: (
      <>
        <p>Usamos fornecedores que tratam dados em nosso nome, sob contrato:</p>
        <ul>
          <li>Meta Platforms (a API dos seus anúncios);</li>
          <li>Cloudflare (hospedagem, filas e armazenamento de arquivos);</li>
          <li>Neon (banco de dados);</li>
          <li>Stripe (pagamentos);</li>
          <li>Resend (envio de e-mails);</li>
          <li>Anthropic (assistente de IA, só quando você usa).</li>
        </ul>
        <p>
          Alguns fornecedores processam dados fora do Brasil; nesses casos adotamos as garantias
          previstas na LGPD para transferência internacional.
        </p>
      </>
    ),
  },
  {
    title: "Segurança",
    body: (
      <p>
        Os tokens de acesso à Meta são guardados criptografados (AES-256-GCM), a comunicação usa
        HTTPS, cada agência é isolada das demais e toda alteração feita nos anúncios fica registrada
        em log de auditoria.
      </p>
    ),
  },
  {
    title: "Por quanto tempo guardamos",
    body: (
      <ul>
        <li>Dados da conta: enquanto a conta estiver ativa.</li>
        <li>
          Leads: pelo prazo de retenção configurado pela agência (padrão de 365 dias), depois são
          apagados automaticamente.
        </li>
        <li>
          Dados importados da Meta: enquanto a conexão existir. Ao desconectar a conta Meta, os
          tokens e os dados importados daquela conexão são apagados.
        </li>
        <li>Registros fiscais e de cobrança: pelo prazo exigido em lei.</li>
      </ul>
    ),
  },
  {
    title: "Seus direitos e exclusão de dados",
    body: (
      <>
        <p>
          Você pode pedir confirmação, acesso, correção, portabilidade, anonimização ou exclusão dos
          seus dados, e revogar consentimentos, escrevendo para {mail(LEGAL.emailPrivacidade)}.
        </p>
        <p>
          Para apagar os dados importados da Meta, remova o {LEGAL.produto} em{" "}
          <em>Configurações do Facebook → Apps e sites</em>. A Meta nos avisa e apagamos tudo
          automaticamente; você acompanha o pedido em{" "}
          <Link to="/exclusao-de-dados" search={{}} className="underline">
            {LEGAL.site.replace("https://", "")}/exclusao-de-dados
          </Link>
          .
        </p>
      </>
    ),
  },
  {
    title: "Cookies",
    body: (
      <p>
        Usamos apenas cookies essenciais para manter você conectado e proteger o login. Não usamos
        cookies de publicidade.
      </p>
    ),
  },
  {
    title: "Contato e alterações",
    body: (
      <p>
        Dúvidas sobre privacidade: {mail(LEGAL.emailPrivacidade)}. Quando esta política mudar de
        forma relevante, avisaremos por e-mail ou dentro do sistema.
      </p>
    ),
  },
];

function PrivacidadePage() {
  return <LegalLayout title="Política de Privacidade" sections={sections} />;
}
